import React, { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  Box,
  Typography,
  Popover,
  Paper
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ShoppingCart,
  ShoppingBag,
  People,
  Inventory,
  VerifiedUser,
  AdminPanelSettings
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

import APP_VERSION from '../version';


const drawerWidth = 240;

const Sidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const hoverCloseTimer = useRef(null);
  const [kioskPublicEnabled, setKioskPublicEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/public/kiosk-enabled')
      .then((res) => {
        if (!cancelled) setKioskPublicEnabled(res.data?.enabled !== false);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const clearHoverCloseTimer = () => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };

  const scheduleHoverClose = () => {
    clearHoverCloseTimer();
    hoverCloseTimer.current = setTimeout(() => {
      setSubmenuAnchor(null);
      setActiveSubmenu(null);
      hoverCloseTimer.current = null;
    }, 300);
  };

  const baseMenuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      submenu: null,
    },
    {
      text: 'Sales (CRM)',
      icon: <ShoppingCart />,
      path: null,
      submenu: [
        { text: 'Quotes', path: '/quotes' },
        { text: 'Orders', path: '/work-orders' },
        { text: 'Order calendar', path: '/work-orders/calendar' },
        { text: 'Shipping', path: '/packing-slips' },
        { text: 'Invoices', path: '/customer-invoices' },
        { text: 'Payments', path: '/customer-payments' },
        { text: 'AR Aging', path: '/reports/ar-aging' },
        { text: 'Sales summary', path: '/reports/sales-summary' },
        { text: 'Customers', path: '/customers' },
      ],
    },
    {
      text: 'Procurement',
      icon: <ShoppingBag />,
      path: null,
      submenu: [
        { text: 'P.O.s', path: '/procurement/po' },
        { text: 'Shipments Received', path: '/procurement/shipments-in' },
        { text: 'Vendor Invoices', path: '/procurement/invoices' },
        { text: 'Vendors', path: '/procurement/vendors' },
      ],
    },
    {
      text: 'Human Resources',
      icon: <People />,
      path: null,
      submenu: [
        { text: 'Employees', path: '/hr' },
        { text: 'Time Entries', path: '/hr/time-entries' },
        { text: 'Timecards', path: '/hr/timecards' },
        { text: 'Payroll Hours', path: '/reports/payroll-hours' },
        { text: 'Kiosk Mode', path: '/time-tracking/kiosk' },
      ],
    },
    {
      text: 'Inventory',
      icon: <Inventory />,
      path: '/inventory',
      submenu: [
        { text: 'Overview', path: '/inventory' },
        { text: 'Parts', path: '/parts' },
        { text: 'Services', path: '/services' },
        { text: 'Products', path: '/products' },
        { text: 'Packing slips', path: '/packing-slips' },
      ],
    },
    {
      text: 'Quality',
      icon: <VerifiedUser />,
      path: '/quality',
      submenu: null,
    },
  ];

  // Add admin menu item only for admin users; omit Kiosk when disabled in system settings
  const menuItems = useMemo(() => {
    const items = baseMenuItems.map((item) => {
      if (item.text === 'Human Resources' && item.submenu && !kioskPublicEnabled) {
        return {
          ...item,
          submenu: item.submenu.filter((sub) => sub.path !== '/time-tracking/kiosk')
        };
      }
      return item;
    });
    try {
      if (isAdmin && typeof isAdmin === 'function' && isAdmin()) {
        items.push({
          text: 'Admin',
          icon: <AdminPanelSettings />,
          path: '/admin',
          submenu: null,
        });
      }
    } catch (error) {
      // Silently fail if isAdmin check fails
      console.error('Error checking admin status:', error);
    }
    return items;
  }, [isAdmin, kioskPublicEnabled]);

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      // Close sidebar on mobile after navigation
      if (window.innerWidth < 768) {
        onClose();
      }
    }
  };

  const handleSubmenuOpen = (event, item) => {
    clearHoverCloseTimer();
    setSubmenuAnchor(event.currentTarget);
    setActiveSubmenu(item.text);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setActiveSubmenu(null);
    clearHoverCloseTimer();
  };

  const handleSubmenuMouseLeave = () => {
    scheduleHoverClose();
  };

  const handleSubmenuMouseEnter = () => {
    clearHoverCloseTimer();
  };

  const handleSubmenuItemClick = (path) => {
    handleNavigation(path);
    handleSubmenuClose();
  };

  const getActiveSubmenu = () => {
    return menuItems.find(item => item.text === activeSubmenu);
  };

  const isActive = (item) => {
    const normalize = (p) => String(p || '').replace(/\/+$/, '') || '/';
    const currentPath = normalize(location.pathname);
    const matchesPath = (candidate) => {
      const base = normalize(candidate);
      if (base === '/') return currentPath === '/';
      return currentPath === base || currentPath.startsWith(`${base}/`);
    };

    if (item.path) {
      return matchesPath(item.path);
    }
    if (item.submenu) {
      return item.submenu.some((sub) => matchesPath(sub.path));
    }
    return false;
  };

  return (
    <>
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: open ? 'block' : 'none',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: '48px',
            height: 'calc(100vh - 48px)',
            position: 'fixed',
            borderRight: '1px solid',
            borderColor: 'rgba(0, 77, 64, 0.18)',
            boxShadow: '4px 0 24px rgba(0, 77, 64, 0.06)',
            backgroundColor: 'background.paper',
            zIndex: 1200,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', mt: 2, px: 1 }}>
            <List dense sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {menuItems.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                  <Box
                    onClick={item.submenu ? (e) => handleSubmenuOpen(e, item) : () => handleNavigation(item.path)}
                    onMouseEnter={item.submenu ? (e) => handleSubmenuOpen(e, item) : undefined}
                    onMouseLeave={item.submenu ? handleSubmenuMouseLeave : undefined}
                    sx={{
                      border: '1px solid',
                      borderColor: isActive(item) ? 'primary.dark' : 'divider',
                      borderRadius: 1,
                      backgroundColor: isActive(item) ? 'primary.main' : 'background.paper',
                      color: isActive(item) ? 'primary.contrastText' : 'text.primary',
                      py: 1.5,
                      px: 2,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                      boxShadow: isActive(item) ? '0 4px 14px rgba(0, 77, 64, 0.2)' : 'none',
                      '&:hover': {
                        backgroundColor: isActive(item) ? 'primary.dark' : 'action.hover',
                        borderColor: isActive(item) ? 'primary.dark' : 'primary.light',
                      },
                    }}
                  >
                    <Typography
                      component="div"
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {item.icon}
                      </Box>
                      {item.text}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
          <Box
            onClick={() => handleNavigation('/')}
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              px: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.85 },
            }}
          >
            <Box
              sx={{
                maxWidth: '90%',
                width: '100%',
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                bgcolor: 'primary.dark',
                color: 'primary.contrastText',
                fontWeight: 700,
                fontSize: '1.1rem',
                letterSpacing: '0.18em',
              }}
              aria-label="Ledger"
            >
              LEDGER
            </Box>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                color: 'text.secondary',
                fontSize: '0.7rem',
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Submenu Popover */}
      <Popover
        open={Boolean(submenuAnchor) && Boolean(activeSubmenu)}
        anchorEl={submenuAnchor}
        onClose={handleSubmenuClose}
        disableEnforceFocus
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            pointerEvents: 'auto',
            border: '1px solid',
            borderColor: 'rgba(0, 77, 64, 0.14)',
            borderRadius: 1,
            boxShadow: '0 8px 28px rgba(0, 77, 64, 0.12)',
            mt: 1,
            overflow: 'hidden',
          },
        }}
      >
        <Paper sx={{ p: 0, bgcolor: 'background.paper' }} onMouseEnter={handleSubmenuMouseEnter} onMouseLeave={handleSubmenuMouseLeave}>
          {getActiveSubmenu()?.submenu?.map((subItem, index) => (
            <Box key={subItem.text}>
              <Box
                onClick={() => handleSubmenuItemClick(subItem.path)}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  backgroundColor: (
                    location.pathname === subItem.path ||
                    (subItem.path !== '/' && location.pathname.startsWith(`${subItem.path}/`))
                  ) ? 'action.selected' : 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Typography
                  component="div"
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: 'text.primary',
                  }}
                >
                  {subItem.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      </Popover>
    </>
  );
};

export default Sidebar;
